
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import moment from "moment";
import httpClient from "../../store/httpClient";
import { OrderIntakeDto } from "@/dtos/orderIntakeDto";
import DatePicker from "@/components/DatePicker.vue";
import RegionTotals from "./RegionTotals.vue";

@Component({
	components: {
		DatePicker,
		RegionTotals,
	},
})
export default class ReportIncomingOrders extends Vue {
	@Prop()
	private tab!: any;
	private groupTab: any = 0;
	private loading: boolean = false;
	private startDate: string = moment().subtract(1, "months").format("YYYY-MM-01");
	private endDate: string = moment(moment().format("YYYY-MM-01"))
		.add(1, "months")
		.subtract(1, "days")
		.format("YYYY-MM-DD");
	private orderIntakes: OrderIntakeDto[] = [];
	private customerHeaders: any = [
		{
			text: "Id",
			align: "left",
			sortable: true,
			value: "customerId",
		},
		{
			text: "Name",
			align: "left",
			sortable: true,
			value: "name",
		},
		{
			text: "Betrag",
			align: "right",
			sortable: true,
			value: "total",
		},
		{
			text: "Währung",
			align: "left",
			sortable: true,
			value: "currency",
		},
	];
	private customerPagination: any = {
		sortBy: "total",
		descending: true,
		rowsPerPage: -1,
	};

	private mounted() {
		if (!this.loading && this.orderIntakes.length <= 0) {
			this.loadOrderReports();
		}
	}

	@Watch("tab")
	private async loadReports() {
		if (this.tab === 0 && !this.loading && this.orderIntakes.length <= 0) {
			await this.loadOrderReports();
		}
	}

	private async loadOrderReports() {
		if (this.loading) {
			return;
		}
		try {
			this.loading = true;
			const response = await httpClient().get(
				`Report/GetOrderIntake?from=${this.startDate}&to=${this.endDate}`
			);
			this.orderIntakes = response.data.orderIntakeDtos;
		} catch (err) {
			console.log("Load report failed.");
			console.log(err);
			this.$store.commit(
				"setSnackbarErrorText",
				"Fehler beim Laden der Reports. Siehe Konsole."
			);
		} finally {
			this.loading = false;
		}
	}

	private getRegionNameFromCountryCode(countryCode: string): string {
		return this.$store.getters.regionNameFromCountryCode(countryCode);
	}

	private get allOrders() {
		return this.orderIntakes.filter((o: OrderIntakeDto) => !o.isMandate);
	}
	private get allMandates() {
		return this.orderIntakes.filter((o: OrderIntakeDto) => o.isMandate);
	}

	private get totalOverall() {
		return (
			this.getTotalPortal("CH") +
			this.getTotalMandate("CHF") +
			this.getTotalPortal("DE") / this.euroRate +
			this.getTotalPortal("AT") / this.euroRate +
			this.getTotalMandate("EUR") / this.euroRate
		);
	}

	private get totalPortal() {
		return (
			this.getTotalPortal("CH") +
			this.getTotalPortal("DE") / this.euroRate +
			this.getTotalPortal("AT") / this.euroRate +
			this.getTotalMandate("EUR") / this.euroRate
		);
	}

	private getTotalPortal(countryCode: string) {
		const orders = this.allOrders.filter(
			(o: OrderIntakeDto) => o.region == this.getRegionNameFromCountryCode(countryCode)
		);
		let totalMoney = 0;
		for (const order of orders) {
			totalMoney += Math.round((order.totalAmount + Number.EPSILON) * 100) / 100;
		}
		return totalMoney;
	}
	private getTotalPortalByCurrency(currency: string) {
		const moneys = this.allOrders.filter((o: OrderIntakeDto) => o.currency === currency);
		let totalMoney = 0;
		for (const money of moneys) {
			totalMoney += Math.round((money.totalAmount + Number.EPSILON) * 100) / 100;
		}
		return totalMoney;
	}
	private getTotalMandate(currency: string) {
		const moneys = this.allMandates.filter((o: OrderIntakeDto) => o.currency === currency);
		let totalMoney = 0;
		for (const money of moneys) {
			totalMoney += Math.round((money.totalAmount + Number.EPSILON) * 100) / 100;
		}
		return totalMoney;
	}

	private get distinctPortalCustomerCount() {
		const distinctIds = [...new Set(this.allOrders.map((o) => o.customerId))];
		return distinctIds.length;
	}
	private get distinctMandateCustomerCount() {
		const distinctIds = [...new Set(this.allMandates.map((o) => o.customerId))];
		return distinctIds.length;
	}

	private get averagePortalPerDay() {
		let dateDiff = Math.abs(moment(this.endDate).diff(moment(this.startDate), "days") + 1);
		return dateDiff > 1 ? this.totalPortal / dateDiff : this.totalPortal;
	}
	private get averagePortalPerOrder() {
		return this.allOrders.length > 1
			? this.totalPortal / this.allOrders.length
			: this.totalPortal;
	}

	private get customerGroupReports() {
		let groupedOrders = this.groupByKey(this.orderIntakes, "customerId");
		let customerSummaries = [];
		for (var key in groupedOrders) {
			customerSummaries.push({
				customerId: key,
				name: `${groupedOrders[key][0].companyName}, ${groupedOrders[key][0].givenName} ${groupedOrders[key][0].lastName}`,
				total: groupedOrders[key].reduce((a: any, b: any) => {
					return a + b["totalAmount"];
				}, 0),
				currency: groupedOrders[key][0].currency,
				isMandate: groupedOrders[key][0].isMandate,
			});
		}
		return customerSummaries.sort((a: any, b: any) => b.total - a.total);
	}

	private get regionGroupReports() {
		let groupedOrders = this.groupByKey(this.orderIntakes, "regionId");
		let regionSummaries = [];
		for (var key in groupedOrders) {
			regionSummaries.push({
				regionId: key,
				name: groupedOrders[key][0].region,
				orderCount: groupedOrders[key].length,
				total: groupedOrders[key].reduce((a: any, b: any) => {
					return a + b["totalAmount"];
				}, 0),
				currency: groupedOrders[key][0].currency,
			});
		}
		return regionSummaries.sort((a: any, b: any) => b.total - a.total);
	}

	private get euroRate() {
		let firstEUROrder = this.orderIntakes.filter(
			(o: OrderIntakeDto) => o.currency === "EUR"
		)[0];
		return firstEUROrder === undefined ? 0.9 : firstEUROrder.currencyRate;
	}

	private groupByKey(data: any, key: any) {
		return data.reduce((storage: any, item: any) => {
			let group = item[key];
			storage[group] = storage[group] || [];
			storage[group].push(item);
			return storage;
		}, {});
	}

	private setStartDate(date: Date) {
		this.startDate = moment(date).format("YYYY-MM-DD");
	}
	private setEndDate(date: Date) {
		this.endDate = moment(date).format("YYYY-MM-DD");
	}

	private setToday() {
		this.startDate = moment().format("YYYY-MM-DD");
		this.endDate = moment().format("YYYY-MM-DD");
	}
	private setThisMonth() {
		this.startDate = moment().format("YYYY-MM-01");
		this.endDate = moment(moment().format("YYYY-MM-01"))
			.add(1, "months")
			.subtract(1, "days")
			.format("YYYY-MM-DD");
	}
}
